/**
 * The typed ID entry contract: the fields staff can type off a card when a barcode will not
 * decode, and the pure mappings that turn those fields into something each surface's *existing*
 * create path already understands.
 *
 * Why this layer exists at all. The server route the old typed form posted to
 * (`checkin/ocr-scan-entry`), its DTO, its verifier and the manual-only `GuestEntryService`
 * behaviour were all removed in leaflogix #22416, so there is no manual check-in endpoint left to
 * call and this module deliberately declares no path and no payload envelope. What is left is the
 * part that was never about the endpoint: which fields a card actually yields, what counts as
 * missing, how a date is read and written without a day shift, and which customer type a document
 * implies at a given location. Those answers are identical on the register and on a phone, so they
 * belong in one place rather than being re-derived twice.
 *
 * Everything here is pure. No transport, no react-query, no store, no surface state. The two
 * mapping functions return *field subsets* to be merged by callers that keep owning their own
 * request: Register's `CustomerRequestBuilder` and Mobile's `toCreateGuestPayload` are not moved,
 * rewritten or imported here, and a later layer's call site delegates to these primitives instead
 * of growing its own copy of them.
 *
 * Importable directly as `@dutchie-pos/requests/checkin/typed-id-entry`, and re-exported by
 * `camera-id-scan.ts`, which stays the single import site for a surface already reading the scan
 * contract.
 */

/**
 * Which document staff say they are typing from. This is a *staff assertion*, not a server
 * classification: nothing decoded the card, so the only thing that knows whether this is a
 * driver's licence or a medical card is the person holding it.
 */
export type IdDocumentType = 'DriverLicense' | 'MedicalCard';

/**
 * Every field an AAMVA-compliant card can yield, named as the check-in pipeline names them.
 *
 * The list is wider than the form: a decoded scan populates all of these, and narrowing the union
 * to only the typed subset would leave the layer that displays a decode — discrepancy review —
 * with no name for the rest.
 */
export const ID_FIELD_NAMES = [
  'FirstName',
  'LastName',
  'MiddleName',
  'IDNumber',
  'DateOfBirth',
  'ExpirationDate',
  'IssueDate',
  'Address1',
  'City',
  'State',
  'PostalCode',
  'Gender',
] as const;

export type IdFieldName = (typeof ID_FIELD_NAMES)[number];

/** Everything the check-in pipeline needs before it can match or create a customer. */
export const ID_REQUIRED_FIELDS: IdFieldName[] = ['FirstName', 'LastName', 'DateOfBirth', 'IDNumber'];

/**
 * What a typed form offers: the four the pipeline requires, plus the expiration.
 *
 * The expiration is editable but not required on purpose — an expired or undated card is a
 * compliance decision for the counter, not a reason to refuse to look the customer up.
 */
export const ID_EDITABLE_FIELDS: IdFieldName[] = [...ID_REQUIRED_FIELDS, 'ExpirationDate'];

/** The fields held as `YYYY-MM-DD`, so a surface knows which ones need a date control. */
export const ID_DATE_FIELDS: IdFieldName[] = ['DateOfBirth', 'ExpirationDate', 'IssueDate'];

export const ID_FIELD_LABELS: Record<IdFieldName, string> = {
  FirstName: 'First name',
  LastName: 'Last name',
  MiddleName: 'Middle name',
  IDNumber: 'ID number',
  DateOfBirth: 'Date of birth',
  ExpirationDate: 'Expiration',
  IssueDate: 'Issue date',
  Address1: 'Address',
  City: 'City',
  State: 'State',
  PostalCode: 'Postal code',
  Gender: 'Gender',
};

/** What staff have typed so far. Partial because a form in progress is the normal state. */
export type IdFieldValues = Partial<Record<IdFieldName, string>>;

/** `YYYY-MM-DD`, or the date half of a serialized `DateTime`. Local midnight, so no day shifts. */
export const toLocalDate = (value?: string | null): Date | null => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(value ?? '');
  if (!parts) {
    return null;
  }

  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(year, month - 1, day);

  // The shape is not enough: the Date constructor rolls a date that does not exist forward, so
  // `1990-02-30` would read back as 2 March and `1990-13-01` as January 1991. These values are a
  // date of birth and an ID expiry, so a silently shifted day is an age or a validity answered
  // wrongly rather than refused. Round-tripping the parts is what rejects them.
  const exists = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

  return exists ? date : null;
};

export const toDateString = (date: Date | null): string => {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return [String(date.getFullYear()), month, day].join('-');
};

/**
 * Whitespace counts as missing. A single space in `IDNumber` is not a value the pipeline can match
 * on, and letting it through the four-field gate sends the server a non-null blank to search with.
 */
export const missingRequiredFields = (values: IdFieldValues): IdFieldName[] =>
  ID_REQUIRED_FIELDS.filter((field) => !values[field]?.trim());

/**
 * A customer type as the location has it configured, structurally: the id, and the two flags that
 * classify it. Both flags are optional *and* nullable because a location's own list is the only
 * authority on them, and a type whose classification the server did not project is a type this
 * module refuses to classify rather than guesses at.
 */
export type ConfiguredCustomerType = {
  CustomerTypeId: number;
  IsMedical?: boolean | null;
  IsRetail?: boolean | null;
};

/**
 * The seeded ids for the two ordinary types, used *only* to recognise them in a location's own
 * configured list. Nothing here sends an id that the location did not configure: these are a
 * lookup key, never a fallback value.
 */
export const MEDICAL_CUSTOMER_TYPE_ID = 1;
export const RECREATIONAL_CUSTOMER_TYPE_ID = 2;

export type SelectCustomerTypeIdArgs = {
  /** What staff said they are typing from. */
  documentType: IdDocumentType;
  /** The location's configured types. `undefined` while settings are still loading. */
  customerTypes: ConfiguredCustomerType[] | null | undefined;
  /** `locationSettings.DefaultCustomerTypeId`. `0`, `null` and `undefined` all mean unset. */
  defaultCustomerTypeId?: number | null;
};

const isMedicalType = (type: ConfiguredCustomerType): boolean => type.IsMedical === true;
const isNonMedicalType = (type: ConfiguredCustomerType): boolean => type.IsMedical === false;
const isRetailType = (type: ConfiguredCustomerType): boolean => type.IsRetail === true;

const onlyMatch = (types: ConfiguredCustomerType[]): ConfiguredCustomerType | undefined =>
  types.length === 1 ? types[0] : undefined;

/**
 * Which customer type a typed card implies at *this* location, or `undefined` when the honest
 * answer is "nobody here can say".
 *
 * Every branch resolves against the location's own configured list, and the returned id is always
 * one that list contains. That is the whole point: customer types are per-dispensary
 * configuration, a location can be medical-only, recreational-only, or run MMIC (10) as its only
 * medical type, and sending an id a location never configured creates a profile the back office
 * cannot classify. So an id is never invented, never inferred from list order, and never
 * hard-coded past a check that the location actually has it.
 *
 * `undefined` is a first-class answer, not a failure. Returned while the list is still loading,
 * and returned when the configuration genuinely does not determine one — several medical types
 * with no default and no seeded `1` among them, for instance. A caller that gets `undefined`
 * should leave the field alone and let the profile form's own default stand, which is exactly what
 * happens today for a customer created by hand.
 *
 * Resolution order, medical card:
 *   1. the location's default, if that default is configured *and* classified medical;
 *   2. the sole medical type, if the location configured exactly one;
 *   3. the seeded medical id (1), if configured and not explicitly classified non-medical;
 *   4. `undefined`.
 *
 * Resolution order, driver's licence:
 *   1. the location's default, if that default is configured *and* classified non-medical;
 *   2. the seeded recreational id (2), if configured and not explicitly classified medical;
 *   3. the sole non-medical *retail* type, if the location configured exactly one;
 *   4. `undefined`.
 *
 * The two orders differ where the configuration makes them differ. A medical-only location's one
 * medical type is unambiguous whatever its id, so it outranks the seed; a recreational location
 * routinely configures several non-medical types (tax-exempt, out-of-state, non-cannabis), so the
 * seeded retail id is the specific answer and "the only non-medical retail type" is the fallback
 * for a location that does not use it.
 *
 * A type whose `IsMedical` the server did not project counts as neither medical nor non-medical,
 * so every branch that reads a *classification* — the default, the sole medical type, the sole
 * non-medical retail type — passes it over. The two seed branches match by id instead, and only an
 * explicitly contradicting classification disqualifies a seed: an unclassified `1` is still the
 * medical type this location configured, and refusing it would leave a medical card with no type
 * at a location that has one.
 */
export const selectCustomerTypeId = ({
  documentType,
  customerTypes,
  defaultCustomerTypeId,
}: SelectCustomerTypeIdArgs): number | undefined => {
  // Not "no types configured" — an empty or absent list is a list that has not loaded, and
  // answering from one would answer from nothing.
  if (!customerTypes?.length) {
    return undefined;
  }

  const configuredDefault =
    defaultCustomerTypeId && defaultCustomerTypeId > 0
      ? customerTypes.find((type) => Number(type.CustomerTypeId) === Number(defaultCustomerTypeId))
      : undefined;

  const configuredSeed = (seedId: number, disqualifies: (type: ConfiguredCustomerType) => boolean) => {
    const seed = customerTypes.find((type) => Number(type.CustomerTypeId) === seedId);

    return seed && !disqualifies(seed) ? Number(seed.CustomerTypeId) : undefined;
  };

  if (documentType === 'MedicalCard') {
    if (configuredDefault && isMedicalType(configuredDefault)) {
      return Number(configuredDefault.CustomerTypeId);
    }

    const soleMedical = onlyMatch(customerTypes.filter(isMedicalType));
    if (soleMedical) {
      return Number(soleMedical.CustomerTypeId);
    }

    // The seed is the medical type by convention, so a location that classified it non-medical has
    // overridden that convention and this branch must not talk past it.
    return configuredSeed(MEDICAL_CUSTOMER_TYPE_ID, isNonMedicalType);
  }

  if (configuredDefault && isNonMedicalType(configuredDefault)) {
    return Number(configuredDefault.CustomerTypeId);
  }

  const seededRecreational = configuredSeed(RECREATIONAL_CUSTOMER_TYPE_ID, isMedicalType);
  if (seededRecreational !== undefined) {
    return seededRecreational;
  }

  const soleRetail = onlyMatch(customerTypes.filter((type) => isNonMedicalType(type) && isRetailType(type)));

  return soleRetail ? Number(soleRetail.CustomerTypeId) : undefined;
};

/**
 * What a typed entry is, as both create mappings read it: the fields, the document staff said they
 * came from, and the already-resolved customer type.
 *
 * `customerTypeId` is passed in rather than resolved here because the configuration it comes from
 * lives in each surface's own store. A caller resolves it with `selectCustomerTypeId` and passes
 * whatever that returned, `undefined` included.
 */
export type TypedIdCreateInput = {
  values: IdFieldValues;
  documentType: IdDocumentType;
  customerTypeId?: number;
};

/**
 * The identification slot a document type writes to. A card number is not a generic field: the
 * profile has a driver's licence slot and a medical-ID slot, each with its own expiration, and the
 * document staff typed from is the only thing that says which one the number belongs in.
 */
const isMedicalDocument = (documentType: IdDocumentType): boolean => documentType === 'MedicalCard';

const trimmed = (values: IdFieldValues, field: IdFieldName): string => values[field]?.trim() ?? '';

/** Present only when it has a value, so `{ ...defaults, ...overrides }` cannot blank a field. */
const whenPresent = <TKey extends string>(key: TKey, value: string): Partial<Record<TKey, string>> =>
  value ? ({ [key]: value } as Record<TKey, string>) : {};

/**
 * Register's new-customer draft, as override keys on `CustomerDetails`.
 *
 * Every key is optional, and a field staff left blank is *absent* rather than empty. Two reasons,
 * and both are about how the register consumes this: the new-customer form merges overrides onto
 * its own `initialValues`, which already supply safe empty defaults, so an empty string adds
 * nothing; and an absent key cannot clear a value a later merge — a prefill, or a loaded profile —
 * already put there. The opposite identification slot is absent for exactly that reason: a typed
 * medical card must not blank a driver's licence number the draft is carrying.
 *
 * `CustomerTypeId` is absent when `selectCustomerTypeId` could not resolve one, which leaves the
 * form's own default standing rather than asserting a type the location may not have.
 */
export type TypedIdDraftOverrides = Partial<{
  FirstName: string;
  LastName: string;
  DOB: string;
  DriversLicenseId: string;
  DLExpirationDate: string;
  MJStateIDNo: string;
  MJExpirationDate: string;
  CustomerTypeId: number;
}>;

export const toTypedIdDraftOverrides = ({
  values,
  documentType,
  customerTypeId,
}: TypedIdCreateInput): TypedIdDraftOverrides => {
  const idNumber = trimmed(values, 'IDNumber');
  const expiration = trimmed(values, 'ExpirationDate');
  const medical = isMedicalDocument(documentType);

  return {
    ...whenPresent('FirstName', trimmed(values, 'FirstName')),
    ...whenPresent('LastName', trimmed(values, 'LastName')),
    ...whenPresent('DOB', trimmed(values, 'DateOfBirth')),
    ...(medical
      ? { ...whenPresent('MJStateIDNo', idNumber), ...whenPresent('MJExpirationDate', expiration) }
      : { ...whenPresent('DriversLicenseId', idNumber), ...whenPresent('DLExpirationDate', expiration) }),
    ...(customerTypeId === undefined ? {} : { CustomerTypeId: customerTypeId }),
  };
};

/**
 * The fields a typed entry contributes to Mobile's `v2/guest/create` body, under that endpoint's
 * own key names.
 *
 * A *subset*, deliberately. `status`, `country_code`, the base location and auth params and every
 * other transport constant stay with the caller that already sends them, because this module maps
 * fields and does not own a request. `toCreateGuestPayload` is untouched; a later call site spreads
 * this over its own body rather than duplicating the mapping.
 *
 * Unlike the register draft, the three required fields and the selected slot's expiration are
 * always present — creating a profile cannot clear anything, and this endpoint wants a null date as
 * an empty string rather than an absent key. The opposite identification slot is still absent: the
 * profile being created has no number in it, and sending a blank one would claim it was checked.
 *
 * `postal_code` is `''` and is the only address field here. A typed entry collects no address —
 * that is scan-only data the backend fills from a decode — and this contributes no address UI and
 * no ZIP normalization of any kind.
 */
export type TypedIdCreateFields = {
  FirstName: string;
  LastName: string;
  DOB: string;
  postal_code: '';
} & Partial<{
  DriversLicenseId: string;
  DLExpirationDate: string;
  MJStateIDNo: string;
  ExpirationDate: string;
  CustomerTypeId: number;
}>;

export const toTypedIdCreateFields = ({
  values,
  documentType,
  customerTypeId,
}: TypedIdCreateInput): TypedIdCreateFields => {
  const idNumber = trimmed(values, 'IDNumber');
  const expiration = trimmed(values, 'ExpirationDate');
  const medical = isMedicalDocument(documentType);

  return {
    FirstName: trimmed(values, 'FirstName'),
    LastName: trimmed(values, 'LastName'),
    DOB: trimmed(values, 'DateOfBirth'),
    ...(medical
      ? { MJStateIDNo: idNumber, ExpirationDate: expiration }
      : { DriversLicenseId: idNumber, DLExpirationDate: expiration }),
    ...(customerTypeId === undefined ? {} : { CustomerTypeId: customerTypeId }),
    postal_code: '',
  };
};
